import type { EngineFill, Fill, Side } from "@trader/shared/Money"
import { BigDecimal, Option } from "effect"

// Backtest vs paper (docs/design/0007): a strategy runner's engine fills (the
// backtest of the strategy, run forward on the same bars) paired with the paper
// account's fills, so a difference between the backtest and paper trading shows
// trade by trade. Plain functions, tested without React.

type Decimal = BigDecimal.BigDecimal

export type FillMatch =
  /** Same bar, side, quantity and price. */
  | "match"
  /** Same trade, filled on a later bar in paper (stop/limit exits act one bar late in paper). */
  | "later"
  | "price"
  | "qty"
  /** The backtest traded; paper didn't (yet, or the order was denied). */
  | "missing"
  /** Paper traded without a backtest trade (e.g. an order sized differently, or a square-off). */
  | "extra"

export interface FillPair {
  readonly side: Side
  readonly backtest: EngineFill | null
  readonly paper: Fill | null
  readonly match: FillMatch
  /** paper price − backtest price (null unless both exist). */
  readonly priceDiff: Decimal | null
}

export interface FillComparison {
  readonly pairs: ReadonlyArray<FillPair>
  readonly matched: number
  readonly total: number
  /** Paper's charges over the paired fills. */
  readonly charges: Decimal
}

/** Engine numbers are analysis figures; this exact conversion is only for showing the difference. */
const toDecimal = (value: number): Decimal => Option.getOrElse(BigDecimal.fromNumber(value), () => BigDecimal.fromBigInt(0n))

const sameQty = (engine: EngineFill, paper: Fill): boolean => BigDecimal.equals(toDecimal(engine.qty), paper.qty)

/**
 * Pairs the fills in time order. A paper fill belongs to a backtest fill when it
 * has the same side and comes on the same bar or later, but before the
 * backtest's next fill (so a fill a bar late pairs, over nights and weekends too,
 * and a missing one never shifts the pairs after it).
 */
export const compareFills = (engineFills: ReadonlyArray<EngineFill>, paperFills: ReadonlyArray<Fill>): FillComparison => {
  const engine = [...engineFills].sort((a, b) => a.time - b.time)
  const paper = [...paperFills].sort((a, b) => a.time - b.time || a.seq - b.seq)
  const used = new Set<number>()
  const pairs: Array<FillPair> = []

  engine.forEach((fill, index) => {
    const nextTime = engine.slice(index + 1).find((next) => next.time > fill.time)?.time ?? Number.POSITIVE_INFINITY
    let found = -1
    for (let i = 0; i < paper.length; i++) {
      const candidate = paper[i]!
      if (candidate.time >= nextTime) break
      if (used.has(i) || candidate.side !== fill.side || candidate.time < fill.time) continue
      found = i
      break
    }
    if (found === -1) {
      pairs.push({ side: fill.side, backtest: fill, paper: null, match: "missing", priceDiff: null })
      return
    }
    used.add(found)
    const candidate = paper[found]!
    const priceDiff = BigDecimal.subtract(candidate.price, toDecimal(fill.price))
    const match: FillMatch = candidate.time !== fill.time
      ? "later"
      : !sameQty(fill, candidate)
      ? "qty"
      : !BigDecimal.isZero(priceDiff)
      ? "price"
      : "match"
    pairs.push({ side: fill.side, backtest: fill, paper: candidate, match, priceDiff })
  })
  paper.forEach((fill, i) => {
    if (!used.has(i)) pairs.push({ side: fill.side, backtest: null, paper: fill, match: "extra", priceDiff: null })
  })
  pairs.sort((a, b) => (a.backtest?.time ?? a.paper!.time) - (b.backtest?.time ?? b.paper!.time))

  const charges = pairs.reduce(
    (sum, pair) => (pair.paper === null ? sum : BigDecimal.sum(sum, pair.paper.fee.total)),
    BigDecimal.fromBigInt(0n)
  )
  return { pairs, matched: pairs.filter((pair) => pair.match === "match").length, total: pairs.length, charges }
}

export const MATCH_LABELS: Readonly<Record<FillMatch, string>> = {
  match: "Same",
  later: "Filled a bar later",
  price: "Different price",
  qty: "Different quantity",
  missing: "Not in paper",
  extra: "Only in paper"
}
